import { useTranslation } from "react-i18next"
import i18n from "i18next"

/**
 * Drop-in replacement for next-intl's useTranslations.
 * Usage: const t = useTranslations("namespace") — identical API to next-intl.
 *
 * Internally delegates to react-i18next, prepending the namespace to each key
 * so that t("key") resolves to t("namespace.key") in the flat translation JSON.
 *
 * The messages also carry next-intl ICU plurals — `{count, plural, =0 {…} other {# …}}` —
 * which i18next cannot parse: its `{`/`}` interpolation mangles the pattern into
 * visible garbage. Those are resolved here, from the raw resource, BEFORE the
 * remaining `{vars}` go through i18next's interpolator.
 */

const ICU_PLURAL = /\{\s*(\w+)\s*,\s*plural\s*,\s*((?:[^{}]|\{[^{}]*\})*)\}/g

/** Pick the matching branch: exact `=N` wins over the CLDR category; `#` becomes the count. */
function selectPluralForm(body: string, count: number, locale: string): string | null {
	const cases: Record<string, string> = {}
	const caseRe = /(=\d+|zero|one|two|few|many|other)\s*\{((?:[^{}]|\{[^{}]*\})*)\}/g
	for (let m = caseRe.exec(body); m; m = caseRe.exec(body)) cases[m[1]] = m[2]
	const form = cases[`=${count}`] ?? cases[new Intl.PluralRules(locale).select(count)] ?? cases.other
	return form === undefined ? null : form.replace(/#/g, String(count))
}

function resolveIcuPlurals(raw: string, params: Record<string, unknown>, locale: string): string {
	return raw.replace(ICU_PLURAL, (match, varName: string, body: string) => {
		const count = Number(params[varName])
		if (!Number.isFinite(count)) return match
		return selectPluralForm(body, count, locale) ?? match
	})
}

/** Raw (un-interpolated) message for a key, from the active language or the fallback. */
function rawMessage(key: string): string | undefined {
	for (const lng of [i18n.language, i18n.options?.fallbackLng].flat()) {
		if (typeof lng !== "string") continue
		const value = i18n.getResource(lng, "translation", key)
		if (typeof value === "string") return value
	}
	return undefined
}

export function useTranslations(namespace?: string) {
	const { t: i18nT } = useTranslation()

	return ((key: string, params?: Record<string, unknown>) => {
		const fullKey = namespace ? `${namespace}.${key}` : key
		const raw = params ? rawMessage(fullKey) : undefined
		if (raw?.includes(", plural,")) {
			const locale = i18n.language || "ca"
			const resolved = resolveIcuPlurals(raw, params as Record<string, unknown>, locale)
			return i18n.services.interpolator.interpolate(resolved, params as Record<string, unknown>, locale, i18n.options.interpolation ?? {})
		}
		return i18nT(fullKey, params as Record<string, string>)
	}) as ReturnType<typeof useTranslation>["t"]
}
